/**
 * layout.ts — Contêiner vertical do Studio (`layout.stack`)
 *
 * Serve para agrupar vários blocos dentro de UMA coluna: chapéu + título +
 * texto + botões, ou um cartão com conteúdo empilhado. Sem ele, cada coluna
 * só aceitava um bloco, o que impedia composições editoriais.
 *
 * Como todo bloco do Studio, tem componente web
 * (`src/lib/studio/render/web/primitiveBlocks.tsx`) e compilador HTML
 * (`supabase/functions/_shared/studio/htmlRenderer.ts`).
 *
 * RELEASE: v1.0.0 - 2026-09-11 - Versão inicial (vocabulário editorial, Fase 2)
 */

import { registerBlocks } from '../blockRegistry';
import type { StudioOutput } from '../types';

/** Entrega em qualquer meio: tela, PDF e e-mail. */
const TODAS: StudioOutput[] = ['web', 'pdf', 'email'];

registerBlocks([
  {
    key: 'layout.stack',
    label: 'Empilhamento',
    family: 'layout',
    icon: 'rows',
    outputs: TODAS,
    container: true,
    props: [
      {
        key: 'gap',
        label: 'Espaço entre os blocos',
        type: 'select',
        defaultValue: 'md',
        options: [
          { value: 'none', label: 'Colado' },
          { value: 'xs', label: 'Mínimo' },
          { value: 'sm', label: 'Pequeno' },
          { value: 'md', label: 'Médio' },
          { value: 'lg', label: 'Grande' },
        ],
      },
      {
        key: 'align',
        label: 'Alinhamento',
        type: 'select',
        defaultValue: 'start',
        options: [
          { value: 'start', label: 'Início' },
          { value: 'center', label: 'Centro' },
          { value: 'end', label: 'Fim' },
          { value: 'stretch', label: 'Ocupar a largura' },
        ],
      },
      {
        key: 'justify',
        label: 'Distribuição vertical',
        type: 'select',
        defaultValue: 'start',
        advanced: true,
        options: [
          { value: 'start', label: 'Em cima' },
          { value: 'center', label: 'No meio' },
          { value: 'end', label: 'Embaixo' },
          { value: 'between', label: 'Espalhado' },
        ],
      },
      { key: 'maxWidth', label: 'Largura máxima', type: 'text', example: '560px' },
      {
        key: 'padding',
        label: 'Respiro interno',
        type: 'select',
        defaultValue: 'none',
        options: [
          { value: 'none', label: 'Nenhum' },
          { value: 'sm', label: 'Pequeno' },
          { value: 'md', label: 'Médio' },
          { value: 'lg', label: 'Grande' },
        ],
      },
      { key: 'backgroundColor', label: 'Cor de fundo', type: 'color' },
      { key: 'textColor', label: 'Cor do texto', type: 'color' },
      {
        key: 'border',
        label: 'Borda',
        type: 'select',
        defaultValue: 'none',
        advanced: true,
        options: [
          { value: 'none', label: 'Sem borda' },
          { value: 'soft', label: 'Discreta' },
          { value: 'accent', label: 'Na cor de destaque' },
          { value: 'left', label: 'Só à esquerda' },
        ],
      },
      {
        key: 'radius',
        label: 'Cantos',
        type: 'select',
        defaultValue: 'none',
        advanced: true,
        options: [
          { value: 'none', label: 'Reto' },
          { value: 'sm', label: 'Leve' },
          { value: 'lg', label: 'Arredondado' },
          { value: 'xl', label: 'Bem arredondado' },
        ],
      },
      {
        key: 'sticky',
        label: 'Acompanhar a rolagem',
        type: 'boolean',
        defaultValue: false,
        
        advanced: true,
        help: 'Fixa a coluna no topo enquanto o restante da seção rola. No celular é desligado sozinho.',
      },
    ],
  },
]);
